import React, { useEffect, useRef, useState } from 'react';

export interface SSOption {
  value: string | number;
  label: string;
}

/**
 * A type-to-search dropdown. Drop-in replacement for a native <select> where the
 * option list can get long (users, devices, etc.). Themed with the app's CSS
 * variables so it works in light and dark mode.
 */
export const SearchableSelect: React.FC<{
  options: SSOption[];
  value: string | number | null | '';
  onChange: (value: string | number | '') => void;
  placeholder?: string;       // text shown in the search box
  emptyLabel?: string;        // if provided, adds a "clear" choice (e.g. "Unassigned")
  disabled?: boolean;
  onOpen?: () => void;        // called when the menu opens (handy for lazy loading)
  style?: React.CSSProperties;
}> = ({ options, value, onChange, placeholder = 'Search…', emptyLabel, disabled, onOpen, style }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hover, setHover] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find(o => String(o.value) === String(value));

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = () => {
    if (disabled) return;
    setOpen(o => {
      const next = !o;
      if (next && onOpen) onOpen();
      return next;
    });
  };

  const choose = (v: string | number | '') => { onChange(v); setOpen(false); setQuery(''); };

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;

  const optStyle = (key: string, active: boolean): React.CSSProperties => ({
    padding: '8px 10px', fontSize: 14, cursor: 'pointer', color: 'var(--c-text)',
    background: hover === key ? 'var(--c-bg)' : active ? 'var(--c-bg)' : 'transparent',
  });

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button type="button" disabled={disabled} onClick={toggle} style={{
        width: '100%', textAlign: 'left', padding: '8px 10px', fontSize: 14, borderRadius: 4,
        border: '1px solid var(--c-border)', background: 'var(--c-surface)',
        color: selected ? 'var(--c-text)' : 'var(--c-textMuted)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : (emptyLabel ?? placeholder)}
        </span>
        <span style={{ color: 'var(--c-textMuted)', fontSize: 11, flexShrink: 0 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', zIndex: 60, top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 6,
          boxShadow: '0 6px 24px rgba(0,0,0,0.18)', maxHeight: 260, overflowY: 'auto',
        }}>
          <div style={{ padding: 6, position: 'sticky', top: 0, background: 'var(--c-surface)', borderBottom: '1px solid var(--c-border)' }}>
            <input
              autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 13, borderRadius: 4,
                border: '1px solid var(--c-border)', background: 'var(--c-bg)', color: 'var(--c-text)',
              }}
            />
          </div>
          {emptyLabel !== undefined && (
            <div onClick={() => choose('')}
              onMouseEnter={() => setHover('__empty')} onMouseLeave={() => setHover(null)}
              style={optStyle('__empty', value === '' || value === null || value === undefined)}>
              {emptyLabel}
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--c-textMuted)' }}>No matches</div>
          )}
          {filtered.map(o => {
            const key = String(o.value);
            return (
              <div key={key} onClick={() => choose(o.value)}
                onMouseEnter={() => setHover(key)} onMouseLeave={() => setHover(null)}
                style={optStyle(key, key === String(value))}>
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
